import {
  ChatRouteStopCandidate,
  ChatRouteStopCandidateColumnValue,
  ChatRouteStopPickAction,
} from "../../types/chat/ChatContent";

import { isRecord, ROUTE_STOP_ENTITY_TYPES } from "./chatWireGuards";
import parseSkippedRouteStops from "./parseSkippedRouteStops";

const isColumnValue = (value: unknown): value is ChatRouteStopCandidateColumnValue =>
  typeof value === "boolean" || typeof value === "number" || typeof value === "string";

const parseColumnValues = (value: unknown): ChatRouteStopCandidate["columnValues"] =>
  isRecord(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, ChatRouteStopCandidateColumnValue] => isColumnValue(entry[1])
        )
      )
    : {};

const parseCandidate = (value: unknown): ChatRouteStopCandidate | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const entityType = ROUTE_STOP_ENTITY_TYPES.find((candidate) => candidate === value.entityType);
  return entityType && typeof value.displayName === "string" && typeof value.entityId === "number"
    ? {
        columnValues: parseColumnValues(value.columnValues),
        displayName: value.displayName,
        entityId: value.entityId,
        entityType,
      }
    : undefined;
};

// `proposal` is the wire's proposal ref, which is where the candidates ride; `action` is its action.
const parseRouteStopPickAction = (
  proposal: Record<string, unknown>,
  action: Record<string, unknown>,
  warnings: string[]
): ChatRouteStopPickAction | undefined => {
  if (
    action.operation !== "add" ||
    typeof action.routeId !== "number" ||
    typeof action.routeName !== "string" ||
    !Array.isArray(proposal.suggestedRecords)
  ) {
    return undefined;
  }
  const parsed = proposal.suggestedRecords.map(parseCandidate);
  const candidates = parsed.filter((candidate): candidate is ChatRouteStopCandidate => !!candidate);
  // A candidate the rep cannot pick is left off rather than failing the list, but never silently.
  if (candidates.length < parsed.length) {
    warnings.push('Dropped malformed "manage_route_stops" suggestedRecords entries');
  }
  if (!candidates.length) {
    return undefined;
  }
  const columns = Array.isArray(proposal.suggestedRecordsColumns)
    ? proposal.suggestedRecordsColumns.filter(
        (column): column is string => typeof column === "string"
      )
    : [];
  return {
    candidates,
    candidatesTitle:
      typeof proposal.suggestedRecordsTitle === "string" ? proposal.suggestedRecordsTitle : "",
    columns,
    routeId: action.routeId,
    routeName: action.routeName,
    ...parseSkippedRouteStops(action.skippedStops, warnings),
    type: "manage_route_stops_pick",
  };
};

export default parseRouteStopPickAction;
